import { GetStaticPaths, GetStaticPropsContext } from 'next'
import { Fragment } from 'react'
import { useTranslation } from 'next-i18next'

import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { GetProps } from '~/utils/pageWrapper/types'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import Metatags from '~/components/Metadata/Metatags'
import { isParamsCountryCodeAndGradeValid } from '~/curriculum/utils/validateCountryCodeGrade'
import MappingQuizQuestionPage from '~/onboarding/pages/MappingQuizQuestion.page'
import { setCurriculumSubjectContentActionCreator } from '~/curriculum/slice/curriculum.slice'
import curriculumSanity from '~/shared/sanity/curriculum'
import useRedirectV2UserToGradeOverview from '~/curriculum/hooks/useRedirectV2UserToGradeOverview'

export default function CurriculumMappingQuizQuestionPage(): React.ReactElement {
  // Redirect Curriculum v2 users off this v1 mapping quiz to their v2 grade overview.
  useRedirectV2UserToGradeOverview()

  const { t } = useTranslation('curriculum')

  return (
    <Fragment>
      <Metatags noindex title={t('mappingQuiz.mappingQuiz')} />
      <MappingQuizQuestionPage />
    </Fragment>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  (store) =>
  async ({ params }: GetStaticPropsContext<{ grade: string; countryCode: string; subject: string }>) => {
    const countryCodeWithGradeLabel = params?.countryCode as string
    const grade = params?.grade as string
    const gradeId = `${countryCodeWithGradeLabel}-${grade}`
    const subjectOrder = params?.subject
    const topicInfo = await curriculumSanity.getSubjectInfoForMappingQuiz(gradeId, Number(subjectOrder))
    const subjectUrl = `/curriculum/${countryCodeWithGradeLabel}/${grade}/${subjectOrder}`
    if (topicInfo) {
      store.dispatch(setCurriculumSubjectContentActionCreator({ ...topicInfo, subjectUrl }))
    }
    if (!isParamsCountryCodeAndGradeValid({ countryCode: countryCodeWithGradeLabel, grade })) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        path: `/curriculum/${countryCodeWithGradeLabel}/${grade}`,
        namespaces: [...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES, 'curriculum'],
      },
      revalidate: process.env.ENVIRONMENT === 'staging' ? 60 : undefined,
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)
